/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn, SelectionMode } from '@fluentui/react/lib/DetailsList';
import { Icon } from '@fluentui/react/lib/Icon';
import { Link } from '@fluentui/react/lib/Link';
import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { AppToEmailStore } from 'src/verticals/azureMigrationModal/stores/AppToEmailStore';

export type AppsToEmailTableProps = TableProps<AppToEmailStore>;

const messages = defineMessages({
    nameColumnHeader: {
        id: 'azureMigrationModal.appsToEmailTable.nameColumnHeader',
        defaultMessage: 'App Name'
    },
    regionColumnHeader: {
        id: 'azureMigrationModal.appsToEmailTable.regionColumnHeader',
        defaultMessage: 'Region'
    },
    collaboratorsColumnHeader: {
        id: 'azureMigrationModal.appsToEmailTable.collaboratorsColumnHeader',
        defaultMessage: 'Collaborators'
    },
    emailSubject: {
        id: 'azureMigrationModal.appsToEmailTable.emailSubject',
        defaultMessage: 'Notice: I am migrating my LUIS apps'
    },
    emailBody: {
        id: 'azureMigrationModal.appsToEmailTable.emailBody',
        defaultMessage:
            'Dear Sir/Madam,%0D%0A%0D%0AI will be migrating my LUIS account to Azure.' +
            'Consequently, you will no longer have access to the following' +
            'app:%0D%0A%0D%0AApp Id: { appId }%0D%0AApp name: { appName }' +
            '%0D%0A%0D%0AThank you%0D%0A%0D%0A'
    },
    emptyTablePlaceholder: {
        id: 'azureMigrationModal.appsToEmailTable.emptyTablePlaceholder',
        defaultMessage: 'You have no apps that have collaborators.'
    }
});

/**
 * Displays a table of Apps and their Collaborators.
 *
 * @param props The input properties for the table.
 */
export const AppsToEmailTable = (props: AppsToEmailTableProps) => {
    const { items, selection, ...rest } = props;
    const { localize } = useLocalization();

    /**
     * Sends email to the collaborators of the selected application
     *
     * @param item the app collaborators item
     */
    const sendEmail = (item: AppToEmailStore): string =>
        `mailto:${item.collaborators.join(',')}?subject=${localize(messages.emailSubject)}&body=${localize(messages.emailBody, {
            appId: item.appId,
            appName: item.appName
        })}`;

    const columns: IColumn[] = [
        {
            key: 'sendEmail',
            name: '',
            fieldName: '',
            minWidth: 20,
            maxWidth: 20,
            isResizable: true,
            isCollapsible: true,
            onRender: (item: AppToEmailStore) => (
                <Stack verticalFill verticalAlign="center">
                    <Link href={sendEmail(item)}>
                        <Icon iconName="Send" />
                    </Link>
                </Stack>
            )
        },
        {
            key: 'appName',
            name: localize(messages.nameColumnHeader),
            fieldName: 'appName',
            minWidth: 100,
            maxWidth: 100,
            isRowHeader: true,
            isResizable: true,
            isCollapsible: false,
            onRender: (item: AppToEmailStore) => (
                <Stack verticalFill verticalAlign="center">
                    {item.hasPredictionResource ? `* ${item.appName}` : item.appName}
                </Stack>
            )
        },
        {
            key: 'region',
            name: localize(messages.regionColumnHeader),
            fieldName: 'region',
            minWidth: 100,
            maxWidth: 100,
            isResizable: true,
            isCollapsible: true,
            onRender: (item: AppToEmailStore) => item.region
        },
        {
            key: 'collaborators',
            name: localize(messages.collaboratorsColumnHeader),
            fieldName: 'collaborators',
            minWidth: 100,
            isResizable: true,
            isCollapsible: true,
            onRender: (item: AppToEmailStore) => (
                <Stack verticalFill verticalAlign="center">
                    {item.collaborators.join(',')}
                </Stack>
            )
        }
    ];

    return (
        <Table<AppToEmailStore>
            items={items}
            columns={columns}
            selection={selection}
            selectionMode={SelectionMode.none}
            emptyPlaceholder={localize(messages.emptyTablePlaceholder)}
            {...rest}
        />
    );
};
